import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";

/**
 * ConfirmDialog — reusable delete/action confirmation modal.
 *
 * @param {boolean}  open       — whether the dialog is visible
 * @param {function} onClose    — called to dismiss without action
 * @param {function} onConfirm  — called when user confirms
 * @param {string}   title      — dialog title
 * @param {string}   message    — dialog body text
 * @param {string}   confirmText — confirm button text (default: "Delete")
 * @param {boolean}  danger      — if true, confirm button is red (default: true)
 * @param {boolean}  loading     — if true, confirm button shows spinner
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message = "This action cannot be undone.",
  confirmText = "Delete",
  danger = true,
  loading = false,
}) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[9990] bg-black/40 backdrop-blur-sm"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 z-[9991] flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-elevated border border-ku-soft/60 w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="flex items-start justify-between p-6 pb-0">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    danger
                      ? "bg-red-50 text-red-500"
                      : "bg-ku-soft text-ku-purple"
                  }`}>
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-gray-900 tracking-tight">
                      {title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed font-medium">
                      {message}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 p-6">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors border-none cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={loading}
                  className={`px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all border-none cursor-pointer disabled:opacity-50 ${
                    danger
                      ? "bg-red-500 hover:bg-red-600 shadow-sm"
                      : "bg-ku-purple hover:bg-ku-violet shadow-sm"
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Working...
                    </span>
                  ) : (
                    confirmText
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConfirmDialog;
